import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  FileText,
  Film,
  Image as ImageIcon,
  Megaphone,
  Package,
  Rocket,
  Save,
  UploadCloud,
  X,
} from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../../components/Input/Input";
import Loader from "../../../components/Loader/Loader";
import Notification from "../../../components/Notification/Notification";

/* ===================== SERVICES / UTILS ===================== */
import { apiGet, apiPost, apiPut } from "../../../apiService";
import { getErrorMessage } from "../../../utils/errorHelper";
import { getRole } from "../../../utils/auth";
import {
  BASE_PATH,
  MAX_FILES,
  MAX_FILE_BYTES,
  MEDIA_ACCEPT,
  fileKind,
} from "./featureUtils";

/* ===================== STYLES ===================== */
import styles from "./FeatureUpdates.module.scss";

const emptyForm = { title: "", description: "", productIds: [], publish: false };

const FeatureUpdateForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const isSuperAdmin = getRole() === "Internal";

  const [form, setForm] = useState(emptyForm);
  const [products, setProducts] = useState([]);
  const [productsOpen, setProductsOpen] = useState(false);
  const productMenuRef = useRef(null);
  const [newFiles, setNewFiles] = useState([]);
  const [existingMedia, setExistingMedia] = useState([]);
  const [removeMediaIds, setRemoveMediaIds] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const notify = useCallback((message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3500);
  }, []);

  /* -------- guard -------- */
  useEffect(() => {
    if (!isSuperAdmin) navigate("/admin/dashboard", { replace: true });
  }, [isSuperAdmin, navigate]);

  /* -------- products (dropdown list) -------- */
  useEffect(() => {
    if (!isSuperAdmin) return;
    (async () => {
      try {
        const res = await apiGet("catalog", "/apimaster/products/dropdown");
        if (res?.status === "success" && Array.isArray(res.data)) {
          setProducts(res.data);
        }
      } catch (error) {
        console.error("Products dropdown fetch failed", error);
      }
    })();
  }, [isSuperAdmin]);

  /* close the products dropdown on outside click / ESC */
  useEffect(() => {
    if (!productsOpen) return undefined;
    const handleClickOutside = (e) => {
      if (productMenuRef.current && !productMenuRef.current.contains(e.target)) {
        setProductsOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setProductsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [productsOpen]);

  /* -------- load the update (edit) — GET /feature-updates/:id -------- */
  useEffect(() => {
    if (!isSuperAdmin || !isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const res = await apiGet("catalog", `/feature-updates/${id}`);
        const fu = res?.data?.featureUpdate;
        if (res?.status === "success" && fu) {
          setForm({
            title: fu.title || "",
            description: fu.description || "",
            productIds: (fu.products || []).map((p) => Number(p.apiMasterId)),
            publish: false,
          });
          setExistingMedia(fu.media || []);
        } else {
          notify(res?.message || "Feature update not found", "error");
        }
      } catch (error) {
        notify(getErrorMessage(error, "Feature update not found"), "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [isSuperAdmin, isEdit, id, notify]);

  /* -------- handlers -------- */
  const toggleProduct = (pid) =>
    setForm((f) => ({
      ...f,
      productIds: f.productIds.includes(pid)
        ? f.productIds.filter((x) => x !== pid)
        : [...f.productIds, pid],
    }));

  const onFilesPicked = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const accepted = [];
    for (const file of picked) {
      if (!fileKind(file)) {
        notify(`"${file.name}" must be an image (png/jpg/gif/webp) or video (mp4/webm/mov)`, "error");
        continue;
      }
      if (file.size > MAX_FILE_BYTES) {
        notify(`"${file.name}" exceeds the 50 MB limit`, "error");
        continue;
      }
      accepted.push(file);
    }
    setNewFiles((prev) => {
      const slots = MAX_FILES - existingMedia.length - prev.length;
      if (accepted.length > slots) {
        notify(`You can attach at most ${MAX_FILES} media files`, "warning");
      }
      return [...prev, ...accepted].slice(0, Math.max(0, MAX_FILES));
    });
  };

  const removeNewFile = (idx) =>
    setNewFiles((prev) => prev.filter((_, i) => i !== idx));

  const removeExisting = (mediaId) => {
    setExistingMedia((prev) => prev.filter((m) => m.id !== mediaId));
    setRemoveMediaIds((prev) => (prev.includes(mediaId) ? prev : [...prev, mediaId]));
  };

  /* -------- submit -------- */
  const validate = () => {
    if (!form.title.trim()) return "Title is required";
    if (!form.description.trim()) return "Description is required";
    if (!isEdit && form.productIds.length === 0)
      return "At least one product is required";
    if (form.publish && form.productIds.length === 0)
      return "Add at least one product before publishing";
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      notify(err, "error");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("description", form.description.trim());
      fd.append("productIds", JSON.stringify(form.productIds));
      newFiles.forEach((file) => fd.append("files", file));

      if (isEdit) {
        if (removeMediaIds.length)
          fd.append("removeMediaIds", JSON.stringify(removeMediaIds));
        const res = await apiPut("catalog", `/feature-updates/${id}`, fd);
        notify(res?.message || "Feature update updated", "success");
      } else {
        fd.append("publish", form.publish ? "true" : "false");
        const res = await apiPost("catalog", "/feature-updates", fd);
        notify(res?.message || "Feature update created", "success");
      }
      setTimeout(() => navigate(BASE_PATH), 600);
    } catch (error) {
      notify(getErrorMessage(error, "Could not save the feature update"), "error");
      setSubmitting(false);
    }
  };

  if (!isSuperAdmin) return null;

  const mediaSlotsUsed = existingMedia.length + newFiles.length;

  return (
    <div className={`${styles["fu-page"]} ${styles["fu-page--form"]}`}>
      {notification && (
        <div className={styles["fu-toast"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["fu-header"]}>
        <div className={styles["fu-header__left"]}>
          <button
            type="button"
            className={styles["fu-back"]}
            onClick={() => navigate(BASE_PATH)}
            title="Back to Feature Updates"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
          </button>
          <div className={styles["fu-icon-wrapper"]}>
            <Megaphone size={24} strokeWidth={2.5} />
          </div>
          <div className={styles["fu-title-group"]}>
            <h2 className={styles["fu-title"]}>
              {isEdit ? "Edit Feature Update" : "New Feature Update"}
            </h2>
            <p className={styles["fu-subtitle"]}>
              {isEdit
                ? "Update the details, products, or media of this announcement."
                : "Create an announcement and optionally publish it now."}
            </p>
          </div>
        </div>
      </div>

      {/* FORM */}
      {loading ? (
        <div className={styles["fu-section-card"]}>
          <Loader />
        </div>
      ) : (
        <form
          className={styles["fu-form"]}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {/* DETAILS */}
          <div className={styles["fu-section-card"]}>
            <div className={styles["fu-card-header"]}>
              <div className={styles["fu-card-icon"]}>
                <FileText size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["fu-card-title"]}>Update Details</h3>
            </div>

            <Input
              label="Title"
              required
              value={form.title}
              maxLength={160}
              placeholder="e.g. Faster PDF export"
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />

            <Input
              type="textarea"
              label="Description"
              required
              value={form.description}
              placeholder="What changed and why it matters..."
              style={{ height: "120px" }}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
            />
          </div>

          {/* PRODUCTS */}
          <div className={styles["fu-section-card"]}>
            <div className={styles["fu-card-header"]}>
              <div className={styles["fu-card-icon"]}>
                <Package size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["fu-card-title"]}>
                Products{isEdit ? "" : " *"}
              </h3>
            </div>
            <p className={styles["fu-hint"]}>
              Select the products this announcement applies to.
            </p>

            <div className={styles["fu-select-wrap"]} ref={productMenuRef}>
              <button
                type="button"
                className={`${styles["fu-select-trigger"]} ${
                  productsOpen ? styles["fu-select-trigger--open"] : ""
                }`}
                onClick={() => setProductsOpen((o) => !o)}
                disabled={products.length === 0}
              >
                {form.productIds.length > 0 ? (
                  <span>
                    {form.productIds.length} product
                    {form.productIds.length === 1 ? "" : "s"} selected
                  </span>
                ) : (
                  <span className={styles["fu-select-placeholder"]}>
                    {products.length === 0
                      ? "No products available"
                      : "Select products..."}
                  </span>
                )}
                <ChevronDown size={16} strokeWidth={2.5} />
              </button>

              {productsOpen && (
                <ul className={styles["fu-select-menu"]}>
                  {products.map((p) => {
                    const pid = Number(p.id);
                    const checked = form.productIds.includes(pid);
                    return (
                      <li key={pid}>
                        <label className={styles["fu-select-option"]}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleProduct(pid)}
                          />
                          <span
                            className={styles["fu-select-checkbox"]}
                            aria-hidden
                          >
                            {checked && <Check size={12} strokeWidth={3} />}
                          </span>
                          <span>{p.name || `#${pid}`}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {form.productIds.length > 0 && (
              <div className={styles["fu-pills"]}>
                {form.productIds.map((pid) => {
                  const prod = products.find((p) => Number(p.id) === pid);
                  return (
                    <span
                      key={pid}
                      className={`${styles["fu-pill"]} ${styles["fu-pill--removable"]}`}
                    >
                      {prod?.name || `#${pid}`}
                      <button
                        type="button"
                        className={styles["fu-pill__remove"]}
                        title="Remove"
                        onClick={() => toggleProduct(pid)}
                      >
                        <X size={12} strokeWidth={3} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* MEDIA */}
          <div className={styles["fu-section-card"]}>
            <div className={styles["fu-card-header"]}>
              <div className={styles["fu-card-icon"]}>
                <ImageIcon size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["fu-card-title"]}>Media</h3>
            </div>
            <p className={styles["fu-hint"]}>
              {mediaSlotsUsed}/{MAX_FILES} files attached · images or videos, up
              to 50 MB each.
            </p>

            {existingMedia.length > 0 && (
              <div className={styles["fu-media-grid"]}>
                {existingMedia.map((m) => (
                  <div key={m.id} className={styles["fu-media"]}>
                    {m.type === "video" ? (
                      <video src={m.url} className={styles["fu-media__thumb"]} muted />
                    ) : (
                      <img src={m.url} alt="" className={styles["fu-media__thumb"]} />
                    )}
                    <button
                      type="button"
                      className={styles["fu-media__remove"]}
                      title="Remove"
                      onClick={() => removeExisting(m.id)}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {newFiles.length > 0 && (
              <ul className={styles["fu-filelist"]}>
                {newFiles.map((f, i) => (
                  <li key={i} className={styles["fu-filelist__item"]}>
                    {fileKind(f) === "video" ? <Film size={16} /> : <ImageIcon size={16} />}
                    <span className={styles["fu-filelist__name"]}>{f.name}</span>
                    <button
                      type="button"
                      className={styles["fu-file-remove"]}
                      onClick={() => removeNewFile(i)}
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <label className={styles["fu-drop"]}>
              <UploadCloud size={20} />
              <span>Click to add images or videos</span>
              <input type="file" multiple accept={MEDIA_ACCEPT} hidden onChange={onFilesPicked} />
            </label>
            {isEdit && (
              <p className={styles["fu-hint"]}>
                New files are appended; removed items are deleted from this post.
              </p>
            )}
          </div>

          {/* PUBLISHING (create only) */}
          {!isEdit && (
            <div className={styles["fu-section-card"]}>
              <div className={styles["fu-card-header"]}>
                <div className={styles["fu-card-icon"]}>
                  <Rocket size={18} strokeWidth={2.5} />
                </div>
                <h3 className={styles["fu-card-title"]}>Publishing</h3>
              </div>
              <label className={styles["fu-checkbox"]}>
                <input
                  type="checkbox"
                  checked={form.publish}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, publish: e.target.checked }))
                  }
                />
                <span>Publish now &amp; broadcast to org admins</span>
              </label>
            </div>
          )}

          {/* ACTIONS */}
          <div className={styles["fu-form-actions"]}>
            <button
              type="button"
              className={styles["fu-btn-secondary"]}
              onClick={() => navigate(BASE_PATH)}
              disabled={submitting}
            >
              <X size={16} strokeWidth={2.5} />
              Cancel
            </button>
            <button
              type="submit"
              className={styles["fu-btn-primary"]}
              disabled={submitting}
            >
              <Save size={16} strokeWidth={2.5} />
              {submitting
                ? "Saving..."
                : isEdit
                  ? "Save Changes"
                  : form.publish
                    ? "Create & Publish"
                    : "Create Draft"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default FeatureUpdateForm;
